import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TitleCard from '@/components/feature/TitleCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getMostViewed, searchTitles } from '@/lib/catalog';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useDocumentMeta(
    query ? `星辰影院-搜索“${query}”` : '星辰影院-影视搜索',
    query
      ? `星辰影院搜索“${query}”的结果，包含符合条件的电影与电视剧资源，支持二次搜索与分类浏览。`
      : '在星辰影院搜索电影、电视剧，输入片名、类型或关键词即可快速找到想看的影视作品。',
    '影视搜索,电影搜索,电视剧搜索,星辰影院',
  );

  const results = query ? searchTitles(query) : [];
  const hotRecommend = getMostViewed(6);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const kw = keyword.trim();
    setParams(kw ? { q: kw } : {});
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="font-medium text-foreground-800">搜索</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              搜索影视
            </h1>

            <form onSubmit={submit} className="mt-5 max-w-2xl">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400">
                <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="输入片名、类型或关键词，如：科幻、星际"
                  className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="shrink-0 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
                >
                  搜索
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          {query && results.length > 0 && (
            <>
              <p className="mb-5 text-sm text-foreground-600">
                关于“<span className="font-semibold text-primary-600">{query}</span>”共找到{' '}
                <span className="font-bold text-primary-600">{results.length}</span> 个结果
              </p>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
                {results.map((item) => (
                  <TitleCard key={item.id} item={item} />
                ))}
              </div>
            </>
          )}

          {!query && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-search-line text-3xl" />
              </span>
              <p className="mt-4 text-base font-medium text-foreground-700">输入关键词开始搜索</p>
              <p className="mt-1 text-sm text-foreground-500">支持片名、类型、地区与简介内容检索</p>
            </div>
          )}

          {query && results.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <p className="mt-4 text-base font-medium text-foreground-700">
                没有找到与“{query}”相关的影视资源
              </p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
            </div>
          )}

          {/* hot recommend */}
          <div className="mt-12">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                热门推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
              {hotRecommend.map((item) => (
                <TitleCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}